<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { TriangleAlert, CircleQuestionMark } from '@lucide/vue';
import Modal from '@/components/Modal.vue';
import { useConfirmStore } from '@/stores/confirm';

const store = useConfirmStore();
const { current } = storeToRefs(store);
const danger = computed(() => !!current.value?.danger);

/** Подтверждение словом: кнопка оживает только на точном совпадении. */
const typed = ref('');
watch(current, () => { typed.value = ''; });
const blocked = computed(() => !!current.value?.requireText && typed.value.trim() !== current.value.requireText);
</script>

<template>
  <Modal :open="!!current" width="440px" @close="store.close(false)">
    <div v-if="current" class="cd">
      <div class="cd-icon" :class="{ danger }">
        <TriangleAlert v-if="danger" />
        <CircleQuestionMark v-else />
      </div>
      <div class="cd-text">
        <h3 class="cd-title">{{ current.title ?? (danger ? 'Are you sure?' : 'Confirm') }}</h3>
        <p class="cd-msg">{{ current.message }}</p>
        <label v-if="current.requireText" class="cd-gate">
          <span>Type <code>{{ current.requireText }}</code> to confirm</span>
          <input v-model="typed" class="field" type="text" autocomplete="off" />
        </label>
      </div>
    </div>
    <template #footer>
      <button class="btn btn-secondary" @click="store.close(false)">{{ current?.cancelText ?? 'Cancel' }}</button>
      <button
        class="btn"
        :class="danger ? 'btn-danger-solid' : 'btn-primary'"
        :disabled="blocked"
        @click="store.close(true)"
      >{{ current?.confirmText ?? (danger ? 'Delete' : 'Confirm') }}</button>
    </template>
  </Modal>
</template>

<style scoped>
.cd { display: flex; gap: 14px; align-items: flex-start; }
.cd-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: var(--c-primary-soft);
  color: var(--c-primary-text);
}
.cd-icon.danger { background: var(--c-danger-soft); color: var(--c-danger-text); }
.cd-icon svg { width: 20px; height: 20px; }
.cd-title { margin: 2px 0 6px; font-size: var(--fs-lg); }
.cd-msg { color: var(--c-text-2); font-size: var(--fs-sm); white-space: pre-line; overflow-wrap: anywhere; }
.cd-gate { display: block; margin-top: 12px; font-size: var(--fs-sm); color: var(--c-text-2); }
.cd-gate code { font-family: var(--font-mono, monospace); color: var(--c-danger-text); }
.cd-gate input { margin-top: 6px; }
</style>
